import {
    ButtonBorderless,
    ButtonPrimary,
    ButtonSwitcher,
    ButtonTertiary,
} from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { AddOnDefinitions, createStripeCheckoutSession, formatSubscriptionType } from "../module"
import * as Icons from "@nova/component-icons"
import { useState } from "react"
import classNames from "classnames"
import { selectUserEmail, selectUserID } from "user/profile/selectors"
import { selectUserFullName } from "features/entity-caching/members/UserManagementSlice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useTranslation } from "@nova/use-translation"
import { formatPrice } from "@nova/util-strings"
import { selectAddOnPaywallType } from "features/entity-caching/licenses/selectors"
import { useAsyncCallback } from "@nova/use-async-callback"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { BillingCycle } from "@nova/constants"
import { hideGlobalModal } from "runlevel/actions"

export const AddOnPaywallModal = () => {
    const { t } = useTranslation()

    const dispatch = useAppDispatch()

    const subscriptionType = useAppSelector(selectAddOnPaywallType())

    const email = useAppSelector(selectUserEmail)
    const userId = useAppSelector(selectUserID)
    const fullName = useAppSelector(selectUserFullName())

    const [quantity, setQuantity] = useState(1)
    const [isAnnual, setIsAnnual] = useState(true)

    const onQuantityIncrement = () => {
        if (quantity === undefined) return
        const newQuantity = quantity + 1
        setQuantity(newQuantity)
    }
    const onQuantityDecrement = () => {
        if (!quantity) return
        const newQuantity = quantity - 1
        setQuantity(newQuantity)
    }

    const { handleCheckout, loading: isLoading } = useAsyncCallback(
        {
            handleCheckout: async () => {
                if (!email || !userId || !fullName) return
                const succeeded = await createStripeCheckoutSession({
                    email,
                    userId,
                    fullName,
                    redirectUrl: encodeURIComponent(window.location.href),
                    billingCycle: isAnnual ? BillingCycle.Year : BillingCycle.Month,
                    quantity,
                    subscriptionType,
                })

                if (!succeeded) {
                    dispatch(
                        setGlobalNotificationContext({
                            status: GlobalNotificationStatus.error,
                            message: "Failed to redirect to checkout page. Please contact support.",
                            timeoutInMs: 3000,
                        })
                    )
                }
            },
        },
        [email, userId, fullName, quantity, subscriptionType, isAnnual, dispatch]
    )

    const {
        paywallMessage,
        perks,
        pricePerMonth,
        pricePerYear,
        icon,
        hasMonthlyPaywall,
        contactSalesReason,
        sampleDataModalName,
        sampleDataButtonTitle,
    } = AddOnDefinitions[subscriptionType]

    const priceToUse = isAnnual ? pricePerYear / 12 : pricePerMonth

    const subtotal = isAnnual ? pricePerYear * quantity : pricePerMonth * quantity

    if (!subscriptionType) throw "Used AddOnPaywallModal without setting the subscription type"
    return (
        <HighPriorityNovaModalContainer
            title={`${formatSubscriptionType(subscriptionType, true)} Add-on`}
            icon={icon}
            closeModal={() => dispatch(hideGlobalModal())}
            className="select-none w-[30rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    {!!sampleDataModalName && (
                        <ButtonBorderless
                            LeftIcon={Icons.AwardStar}
                            leftIconClassName="text-lazeryellow"
                            title={sampleDataButtonTitle || "Try Sample Data"}
                            onClick={() =>
                                dispatch(
                                    initializeCenterModalForComponentAction(sampleDataModalName!)
                                )
                            }
                        />
                    )}
                    <ButtonTertiary
                        title="Maybe Later"
                        onClick={() => dispatch(hideGlobalModal())}
                        className="ml-auto"
                    />
                    <ButtonPrimary
                        title="Go to Checkout"
                        disabled={!quantity}
                        isLoading={isLoading}
                        onClick={handleCheckout}
                        RightIcon={Icons.ArrowRight}
                    />
                </div>
            }
        >
            <h5>{t(paywallMessage || "")}</h5>
            <ul className="list-disc text-gray-300 pl-4 mt-1">
                {perks.map((perk, idx) => (
                    <li key={idx}>
                        <h5>{t(perk)}</h5>
                    </li>
                ))}
            </ul>
            <div className="w-full border-b border-gray-700 my-[1.125rem]" />
            <div className="flex items-center justify-between">
                <h4>{t(`${formatSubscriptionType(subscriptionType, true)} Add-on`)}</h4>
                {hasMonthlyPaywall !== false && (
                    <ButtonSwitcher
                        primaryLabel="Annual"
                        secondaryLabel="Monthly"
                        onPrimaryClick={() => setIsAnnual(true)}
                        onSecondaryClick={() => setIsAnnual(false)}
                    />
                )}
            </div>
            <div className="flex items-center justify-between mt-2">
                <div>
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 flex items-center border border-gray-500 rounded-lg w-max px-1">
                            <Icons.Minus
                                className="cursor-pointer text-gray-100 hover:text-gray-0"
                                onClick={onQuantityDecrement}
                            />
                            <h5 className="px-3 mx-1 border-r border-l border-gray-500 select-none">
                                {quantity}
                            </h5>
                            <Icons.Plus
                                className="cursor-pointer text-gray-100 hover:text-gray-0"
                                onClick={onQuantityIncrement}
                            />
                        </div>
                        <div className="flex flex-col self-end">
                            <small className="mono">{t(`x ${formatPrice(priceToUse)}/mo`)}</small>
                            <small
                                className={classNames("text-gray-300", { "opacity-0": !isAnnual })}
                            >
                                {t("billed annually")}
                            </small>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col">
                    <small className="self-end mono">{t(`${formatPrice(subtotal)} USD`)}</small>
                    <small className="text-gray-300 self-end">
                        {t("taxes calculated upon checkout")}
                    </small>
                </div>
            </div>
            {!!contactSalesReason && (
                <div className="flex gap-1 mt-4">
                    <h5
                        className="text-blue-500 hover:text-blue-200 underline cursor-pointer"
                        onClick={() =>
                            window.open("mailto:help@mapnova.com", "_blank", "noopener,noreferrer")
                        }
                    >
                        Contact sales
                    </h5>
                    <h5 className="text-gray-100">{contactSalesReason}.</h5>
                </div>
            )}
        </HighPriorityNovaModalContainer>
    )
}
